import React, { useState } from 'react';
import useUserStore from '../stores/useUserStore';
import DownloadTab from './DownloadTab';
// Statistics tab body = the existing telemetry charts/labels (kept in JS).
import FirmwareLabels from '../Admin/FirmwareLabels';
import PageLayout from '../components/ui/PageLayout';
import { Tabs } from '../components/ui/Tabs';
import { Switch } from '../components/ui/Switch';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';

type Tab = 'download' | 'statistics';

const FirmwarePage: React.FC = () => {
  const hasCapability = useUserStore((s) => s.hasCapability);
  const canManage = hasCapability('manufacturing'); // admin-bypassed
  const canDelete = hasCapability('manager'); // permanent delete — a tier above manage (admin-bypassed)
  const [tab, setTab] = useState<Tab>('download');
  const [manageMode, setManageMode] = useState(false);

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4">
      {/* Shared header */}
      <div className="flex items-center gap-3 mb-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          fill="currentColor"
          viewBox="0 0 16 16"
          className="text-primary drop-shadow-[0_0_8px_rgba(255,204,51,0.6)]"
        >
          <path d="M5 0a.5.5 0 0 1 .5.5V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2A2.5 2.5 0 0 1 14 4.5h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14a2.5 2.5 0 0 1-2.5 2.5v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14A2.5 2.5 0 0 1 2 11.5H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2A2.5 2.5 0 0 1 4.5 2V.5A.5.5 0 0 1 5 0zm-.5 3A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13h7a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 11.5 3h-7zM5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3zM6.5 6a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
        </svg>
        <h1 className="text-2xl font-bold text-white">Firmware</h1>
      </div>
      <p className="text-on-surface-variant text-sm mb-4">
        {tab === 'download' ? 'Download the current firmware builds for your drone model' : 'Firmware versions across the fleet'}
      </p>

      {/* Top tab bar + Manage toggle */}
      <div className="flex items-center mb-5">
        <Tabs
          label="Firmware sections"
          tabs={[{ key: 'download', label: 'Download' }, { key: 'statistics', label: 'Statistics' }]}
          value={tab}
          onChange={(k) => setTab(k as Tab)}
        />
        <Appear show={!!(canManage && tab === 'download')} as="div" className="ml-auto pr-1">
          <Switch
            label="Manage"
            labelSide="start"
            checked={manageMode}
            onChange={(e) => setManageMode(e.target.checked)}
          />
        </Appear>
      </div>

      {tab === 'download' ? (
        <DownloadTab canManage={canManage} canDelete={canDelete} manageMode={manageMode} />
      ) : (
        <FadeIn>
          <FirmwareLabels canManage={canManage} />
        </FadeIn>
      )}
    </PageLayout>
  );
};

export default FirmwarePage;
